import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "csvUploadArea", "mainDropZone", "fileInput", "fileInfo", "fileName", "fileSize", "uploadButton", "startFreshDescription"]
  static values = {
    open: Boolean,
    newQuoteUrl: String,
    selectedSecurityIds: Array,
    maxSecuritiesLimit: Number
  }

  connect() {
    console.log('Quote creation modal controller connected')

    // Listen for custom event to open modal
    this.openModalHandler = (event) => {
      this.open(event)
    }
    document.addEventListener('open-quote-creation-modal', this.openModalHandler)

    // Handle drag and drop on the drop zone
    this.setupDragAndDrop()
  }

  disconnect() {
    // Clean up event listener
    if (this.openModalHandler) {
      document.removeEventListener('open-quote-creation-modal', this.openModalHandler)
    }
  }

  setupDragAndDrop() {
    // No longer needed since we only have the main drop zone
  }

  open(event) {
    console.log('Open method called', event)

    if (event && event.preventDefault) {
      event.preventDefault()
    }

    // Handle selected securities from the event detail if available
    if (event && event.detail && event.detail.selectedIsins) {
      this.selectedSecurityIsins = event.detail.selectedIsins
      // Update the new quote URL to include the selected ISINs
      if (this.selectedSecurityIsins.length > 0) {
        const isinsParam = this.selectedSecurityIsins.join(',')
        const baseUrl = this.newQuoteUrlValue.split('?')[0]
        this.currentNewQuoteUrl = `${baseUrl}?isins=${encodeURIComponent(isinsParam)}`
      } else {
        this.currentNewQuoteUrl = this.newQuoteUrlValue
      }
    } else {
      this.selectedSecurityIsins = []
      this.currentNewQuoteUrl = this.newQuoteUrlValue
    }

    this.openValue = true

    // Show the modal (the element itself is the modal)
    this.element.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')

    // Update the start fresh description based on selected securities
    this.updateStartFreshDescription()

    // Reset the modal state
    this.resetToMain()
    this.clearFile()
  }

  close(event) {
    if (event) event.preventDefault()
    this.openValue = false

    // Hide the modal (the element itself is the modal)
    this.element.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
  }

  stopPropagation(event) {
    // Prevent modal from closing when clicking on modal content
    event.stopPropagation()
  }

  startFresh(event) {
    event.preventDefault()
    // Navigate to the new quote page (with any selected securities)
    window.location.href = this.currentNewQuoteUrl || this.newQuoteUrlValue
  }

  showCsvUpload() {
    if (this.hasCsvUploadAreaTarget) {
      this.csvUploadAreaTarget.classList.remove('hidden')
    }
  }

  resetToMain(event) {
    if (event) event.preventDefault()
    if (this.hasCsvUploadAreaTarget) {
      this.csvUploadAreaTarget.classList.add('hidden')
    }
    this.clearFile()
  }

  triggerFileInput(event) {
    event.preventDefault()
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.showError('Please select a CSV file.')
      return
    }

    // Validate file size (1MB limit)
    const maxSize = 1 * 1024 * 1024 // 1MB in bytes
    if (file.size > maxSize) {
      this.showError('File size must be less than 1MB.')
      return
    }

    // Store the file and update UI
    this.selectedFile = file
    this.displayFileInfo(file)
    this.showCsvUpload() // Show the expanded area
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.disabled = false
    }
  }

  displayFileInfo(file) {
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
    }
    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    }
    if (this.hasFileInfoTarget) {
      this.fileInfoTarget.classList.remove('hidden')
    }
  }

  removeFile(event) {
    event.preventDefault()
    this.clearFile()
  }

  clearFile() {
    this.selectedFile = null
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ''
    }
    if (this.hasFileInfoTarget) {
      this.fileInfoTarget.classList.add('hidden')
    }
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.disabled = true
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  async processAndCreateQuote(event) {
    event.preventDefault()

    if (!this.selectedFile) {
      this.showError('Please select a CSV file.')
      return
    }

    // Disable the button and show loading state
    if (!this.hasUploadButtonTarget) return

    const button = this.uploadButtonTarget
    const originalText = button.innerHTML
    button.disabled = true
    button.innerHTML = 'Processing...'

    try {
      // Parse the CSV file
      const csvText = await this.readFileAsText(this.selectedFile)
      const isins = await this.parseCSV(csvText)

      if (isins.length === 0) {
        throw new Error('No valid ISINs found in CSV file.')
      }

      if (isins.length > this.maxSecuritiesLimitValue) {
        throw new Error(`CSV contains more than ${this.maxSecuritiesLimitValue} securities. Please reduce the number and try again.`)
      }

      // Combine CSV ISINs with any pre-selected securities
      let allIsins = [...isins]
      if (this.selectedSecurityIsins && this.selectedSecurityIsins.length > 0) {
        // Add pre-selected ISINs, avoiding duplicates
        this.selectedSecurityIsins.forEach(isin => {
          if (!allIsins.includes(isin)) {
            allIsins.push(isin)
          }
        })
      }

      // Check total limit after combining
      if (allIsins.length > this.maxSecuritiesLimitValue) {
        throw new Error(`Total securities (${allIsins.length}) exceed the limit of ${this.maxSecuritiesLimitValue}. Please reduce selections.`)
      }

      // Create the quote with all ISINs
      const isinsParam = allIsins.join(',')
      window.location.href = `${this.newQuoteUrlValue.split('?')[0]}?isins=${encodeURIComponent(isinsParam)}`

    } catch (error) {
      console.error('Error processing CSV:', error)
      this.showError(error.message || 'Failed to process CSV file.')

      // Restore button state
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = (e) => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  }

  async parseCSV(csvText) {
    const lines = csvText.split('\n').map(line => line.trim()).filter(line => line)

    if (lines.length < 2) {
      throw new Error('CSV file must have a header row and at least one data row.')
    }

    // Find the ISIN column (case insensitive)
    const header = lines[0].split(',').map(col => col.trim().replace(/"/g, ''))
    const isinColumnIndex = header.findIndex(col => 
      col.toLowerCase() === 'isin' || col.toLowerCase() === 'isins'
    )

    if (isinColumnIndex === -1) {
      throw new Error('CSV file must have a column header named "ISIN" or "ISINs" (case insensitive).')
    }

    // Extract ISINs from the data rows
    const isins = []
    for (let i = 1; i < lines.length; i++) {
      const columns = lines[i].split(',').map(col => col.trim().replace(/"/g, ''))
      if (columns[isinColumnIndex]) {
        const isin = columns[isinColumnIndex].trim()
        if (isin && !isins.includes(isin)) {
          isins.push(isin)
        }
      }
    }

    if (isins.length === 0) {
      throw new Error('No valid ISINs found in the CSV file.')
    }

    return isins
  }

  downloadCsvTemplate(event) {
    event.preventDefault()

    // Download the actual template file from the server
    const link = document.createElement('a')
    link.href = '/templates/quote_isins_template.csv'
    link.download = 'quote_isins_template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  handleMainDragOver(event) {
    event.preventDefault()
    if (this.hasMainDropZoneTarget) {
      this.mainDropZoneTarget.classList.add('border-info-300', 'bg-info-50')
    }
  }

  handleMainDragLeave(event) {
    event.preventDefault()
    if (this.hasMainDropZoneTarget && !this.mainDropZoneTarget.contains(event.relatedTarget)) {
      this.mainDropZoneTarget.classList.remove('border-info-300', 'bg-info-50')
    }
  }

  handleMainDrop(event) {
    event.preventDefault()

    if (this.hasMainDropZoneTarget) {
      this.mainDropZoneTarget.classList.remove('border-info-300', 'bg-info-50')
    }

    const files = event.dataTransfer.files
    if (files.length > 0) {
      // Process the file and show the expanded area
      this.handleFileSelect({ target: { files } })
    }
  }

  updateStartFreshDescription() {
    if (this.hasStartFreshDescriptionTarget) {
      const count = this.selectedSecurityIsins ? this.selectedSecurityIsins.length : 0
      if (count > 0) {
        const securityText = count === 1 ? 'security' : 'securities'
        this.startFreshDescriptionTarget.textContent = `Build a new quote from ${count} selected ${securityText}.`
      } else {
        this.startFreshDescriptionTarget.textContent = 'Build a new quote by searching for securities.'
      }
    }
  }

  showError(message) {
    // You can implement a toast notification here
    // For now, using alert
    alert(message)
  }
}